// Rows as they travel over JSON between the API and the clients (camelCase, ISO dates,
// bigint sync_seq as a string). The same shapes are stored in Dexie (docs/db.md §8).
import { z } from 'zod';
import { ExampleListSchema, MediaListSchema, PartOfSpeechSchema, RelatedListSchema, TranslationListSchema, } from './collections.js';
import { DICTIONARY_ROLES, SHARE_STATUSES } from './constants.js';
export const IsoDateSchema = z.string().min(1);
export const UuidSchema = z.string().min(1);
export const LangCodeSchema = z.string().min(2).max(12);
export const LexemeRowSchema = z.object({
    id: UuidSchema,
    lang: LangCodeSchema,
    headword: z.string(),
    headwordLower: z.string(),
    headwordNormalized: z.string(),
    partOfSpeech: PartOfSpeechSchema.nullable(),
    related: RelatedListSchema,
    media: MediaListSchema,
    // spellings (headword_lower form) users typed that the fill job canonicalized to this word:
    // lookup keys, never shown on the card (docs/db.md §3)
    aliases: z.array(z.string()),
    // the basics of the card (part of speech) are written: the spelling is final
    filledAt: IsoDateSchema.nullable(),
    // the related words are written; NULL while they are being built after the basics
    detailsFilledAt: IsoDateSchema.nullable(),
    // the worker gave up on the stage still missing (basics, then details)
    fillFailedAt: IsoDateSchema.nullable(),
    audioFetchedAt: IsoDateSchema.nullable(),
    createdAt: IsoDateSchema,
    updatedAt: IsoDateSchema,
});
export const GlossRowSchema = z.object({
    id: UuidSchema,
    lexemeId: UuidSchema,
    baseLang: LangCodeSchema,
    transcription: z.string().nullable(),
    // usage, formation and origin in base_lang (docs/db.md §4); shared only, no entry override
    description: z.string().nullable(),
    translations: TranslationListSchema,
    examples: ExampleListSchema,
    // the basics: transcription and translations
    filledAt: IsoDateSchema.nullable(),
    // the description and examples; NULL while they are being built after the basics
    detailsFilledAt: IsoDateSchema.nullable(),
    fillFailedAt: IsoDateSchema.nullable(),
    examplesVoicedAt: IsoDateSchema.nullable(),
    createdAt: IsoDateSchema,
    updatedAt: IsoDateSchema,
});
export const EntryRowSchema = z.object({
    id: UuidSchema,
    dictionaryId: UuidSchema,
    lexemeId: UuidSchema,
    createdBy: UuidSchema.nullable(),
    transcription: z.string().nullable(),
    partOfSpeech: PartOfSpeechSchema.nullable(),
    translations: TranslationListSchema.nullable(),
    examples: ExampleListSchema.nullable(),
    related: RelatedListSchema.nullable(),
    createdAt: IsoDateSchema,
    updatedAt: IsoDateSchema,
    deletedAt: IsoDateSchema.nullable(),
    syncSeq: z.string(),
});
export const DictionaryRowSchema = z.object({
    id: UuidSchema,
    ownerId: UuidSchema,
    targetLang: LangCodeSchema,
    baseLang: LangCodeSchema,
    createdAt: IsoDateSchema,
});
export const DictionaryRoleSchema = z.enum(DICTIONARY_ROLES);
// A person who can write into the dictionary: the owner and every accepted teacher. Names only
// (no e-mails): the card shows who added a word (docs/project.md §7).
export const DictionaryMemberSchema = z.object({ id: UuidSchema, name: z.string().nullable() });
export const DictionarySummarySchema = DictionaryRowSchema.extend({
    wordCount: z.number().int().nonnegative(),
    role: DictionaryRoleSchema,
    owner: DictionaryMemberSchema,
    members: z.array(DictionaryMemberSchema),
});
export const UserRowSchema = z.object({
    id: UuidSchema,
    email: z.string(),
    name: z.string().nullable(),
    locale: z.string(),
    nativeLang: LangCodeSchema,
    createdAt: IsoDateSchema,
});
export const SessionRowSchema = z.object({
    id: UuidSchema,
    userAgent: z.string().nullable(),
    ip: z.string().nullable(),
    createdAt: IsoDateSchema,
    lastUsedAt: IsoDateSchema,
    expiresAt: IsoDateSchema,
    current: z.boolean(),
});
export const ShareStatusSchema = z.enum(SHARE_STATUSES);
// The token travels only in the invite e-mail, never in list responses.
export const ShareRowSchema = z.object({
    id: UuidSchema,
    dictionaryId: UuidSchema,
    email: z.string(),
    userId: UuidSchema.nullable(),
    status: ShareStatusSchema,
    createdAt: IsoDateSchema,
    acceptedAt: IsoDateSchema.nullable(),
    revokedAt: IsoDateSchema.nullable(),
});
//# sourceMappingURL=rows.js.map